import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
    Input,
} from "@material-tailwind/react";
import { useState, useEffect } from "react";
import Toaster from "../../components/Toaster";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector, useDispatch } from "react-redux";
import { AddProductAvailableVendor, resetStatus, fetchVendor, fetchProductVarient } from "@/store/slices/productSlice";
import ShowLoader from "@/components/Loader";
import Select from 'react-select';
import { fetchCity, fetchCountry } from "@/store/slices/internalUsersSlice";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";

const schema = (t) => yup.object().shape({
    product_varient_id: yup.number()
        .typeError(t("errors.available_vendors.product_varient_id.typeError"))
        .required(t("errors.available_vendors.product_varient_id.required")),
    vendor_ids: yup.array()
        .of(yup.number().typeError(t("errors.available_vendors.vendor_ids.typeError")))
        .min(1, t("errors.available_vendors.vendor_ids.min"))
        .required(t("errors.available_vendors.vendor_ids.required")),
    country_id: yup.number()
        .typeError(t("errors.available_vendors.country_id.typeError"))
        .required(t("errors.available_vendors.country_id.required")),
    city_id: yup.number()
        .typeError(t("errors.available_vendors.city_id.typeError"))
        .required(t("errors.available_vendors.city_id.required")),
    price: yup.number()
        .typeError(t("errors.available_vendors.price.typeError"))
        .positive(t("errors.available_vendors.price.positive"))
        .required(t("errors.available_vendors.price.required")),
    currency_code: yup
        .string()
        .trim()
        .uppercase()
        .length(3, t("errors.available_vendors.currency_code.length"))
        .required(t("errors.available_vendors.currency_code.required")),
});


export function ProductAvailableVendor({ open, handler, productId = null }) {

    const dispatch = useDispatch();
    const { t } = useTranslation('product');
    const { t: ct } = useTranslation('common');
    const [isError, setIsError] = useState("");
    const { loading, vendors, product_varients } = useSelector((state) => state.product);
    const { countries, cities } = useSelector((s) => s.internalUser);

    const defaultValues = {
        product_id: null,
        product_varient_id: null,
        vendor_ids: [],
        country_id: null,
        city_id: null,
        price: null,
        currency_code: null
    };

    const { register, handleSubmit, setValue, watch, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema(t)),
        defaultValues: defaultValues
    });

    const variantOptions = (product_varients || []).map((variant) => ({
        ...variant,
        label: `${variant.label} (${variant.currency_code})`,
    }));

    useEffect(() => {
        dispatch(fetchVendor());
        dispatch(fetchCountry());
        if (productId) {
            dispatch(fetchProductVarient(productId));
            setValue('product_id', productId);
        }
    }, [dispatch, open]);

    const close = () => {
        reset();
        handler();
    }

    const onSubmit = async (data) => {
        try {
            const result = await dispatch(AddProductAvailableVendor(data)).unwrap();
            if (result.status) {
                Toaster.success(t("availableVendor.success"));
                dispatch(resetStatus());
                close();
            } else {
                setIsError(result.message || ct("somethingWentWrong"));
                setTimeout(() => {
                    setIsError('');
                }, 8000);
                // Toaster.error(result.message || ct("somethingWentWrong"));
                dispatch(resetStatus());
            }
        } catch (err) {
            setIsError(err?.message || t("availableVendor.failed"));
            setTimeout(() => {
                setIsError('');
            }, 8000);
            // Toaster.error(err?.message || t("availableVendor.failed"));
            dispatch(resetStatus());
        }
    }

    return (
        <Dialog open={open} handler={close}>
            {loading && <ShowLoader />}
            <ScrollToTop />
            <div className="max-h-[100vh] overflow-y-auto px-4 pt-4 pb-6">
                <DialogHeader>{t("availableVendor.title")}</DialogHeader>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogBody>
                        {isError &&
                            <div class="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                                <span class="font-medium">{ct("oops")}</span> {isError}
                            </div>
                        }
                        <div className="grid grid-cols-1 xl:grid-cols-1 gap-6">
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t("availableVendor.productVariantLabel")} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t("availableVendor.productVariantPlaceholder")}
                                    onChange={(product_varient) => {
                                        setValue('product_varient_id', product_varient.value, { shouldValidate: true });
                                    }}
                                    options={variantOptions}
                                    value={variantOptions.find(opt => opt.value === watch('product_varient_id')) || null}
                                    isClearable
                                />
                                {errors.product_varient_id && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.product_varient_id.message}
                                    </p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t("availableVendor.vendorLabel")} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t("availableVendor.vendorPlaceholder")}
                                    onChange={(selectedOptions) => {
                                        const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                                        setValue('vendor_ids', selectedValues, { shouldValidate: true });
                                    }}
                                    options={vendors}
                                    value={
                                        vendors?.filter(opt => (watch('vendor_ids') || []).includes(opt.value)) || []
                                    }
                                    isClearable
                                    isMulti
                                />

                                {errors.vendor_ids && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.vendor_ids.message}
                                    </p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {ct("country")} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={ct("selectCountry")}
                                    onChange={(country) => {
                                        setValue('country_id', country.value, { shouldValidate: true });
                                        dispatch(fetchCity(country?.value));
                                    }}
                                    options={countries}
                                    value={countries?.find(opt => opt.value === watch('country_id')) || null}
                                    isClearable
                                />
                                {errors.country_id && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.country_id.message}
                                    </p>
                                )}
                            </div>
                            {/* City */}
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" className="font-medium">
                                    {ct("city")} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={ct("selectCity")}
                                    onChange={(city) =>
                                        setValue(`city_id`, city?.value || null, { shouldValidate: true })
                                    }
                                    options={cities}
                                    value={cities?.find((opt) => opt.value === watch(`city_id`)) || null}
                                    isClearable
                                />
                                {errors.city_id && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.city_id.message}
                                    </p>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Currency */}
                                <div className="flex flex-col gap-1">
                                    <Typography
                                        variant="small"
                                        color="blue-gray"
                                        className="mb-2 font-medium"
                                    >
                                        {t("currencyCodeLabel")} <span className="text-red-500">*</span>
                                    </Typography>

                                    <Select
                                        placeholder={t("currencyCodePlaceholder")}
                                        options={[
                                            { value: "SAR", label: "SAR" },
                                            { value: "INR", label: "INR" },
                                        ]}
                                        onChange={(currency) =>
                                            setValue("currency_code", currency?.value || null, {
                                                shouldValidate: true,
                                            })
                                        }
                                        value={
                                            [
                                                { value: "SAR", label: "SAR" },
                                                { value: "INR", label: "INR" },
                                            ].find(
                                                (opt) => opt.value === watch("currency_code")
                                            ) || null
                                        }
                                        isClearable
                                    />

                                    {errors.currency_code && (
                                        <p className="text-red-500 text-sm mt-1">
                                            {errors.currency_code.message}
                                        </p>
                                    )}
                                </div>

                                {/* Price */}
                                <div className="flex flex-col gap-1">
                                    <Typography
                                        variant="small"
                                        color="blue-gray"
                                        className="mb-2 font-medium"
                                    >
                                        {ct("price")} <span className="text-red-500">*</span>
                                    </Typography>

                                    <Input
                                        className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                        labelProps={{
                                            className: "before:content-none after:content-none",
                                        }}
                                        min="0"
                                        type="number"
                                        placeholder={ct("enterPrice")}
                                        {...register("price")}
                                    />

                                    {errors.price && (
                                        <p className="text-red-500 text-sm mt-1">
                                            {errors.price.message}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={close}
                                className="rounded-lg"
                            >
                                {ct("cancel")}
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white">
                                {loading ? ct("saving") : ct("save")}
                            </Button>

                        </div>
                    </DialogFooter>
                </form>
            </div>
        </Dialog>
    );
}

export default ProductAvailableVendor;
